/**
 * Chart.js v2 → v3 Adapter
 *
 * This adapter transforms Chart.js v2 configurations to work with Chart.js v3.
 * It handles option transformations, chart type mappings, and callback adaptations.
 */

import {
    Chart,
    ChartType as ChartJsType,
    ChartOptions as ChartJsOptions,
    TooltipItem,
    ChartEvent,
    ActiveElement,
    LegendItem,
    Plugin,
} from 'chart.js';

import {
    ChartOptionsV2,
    ChartScalesSettings,
    ChartXAxe,
    ChartYAxe,
    ChartTooltipOptions,
    ChartTooltipCallback,
    ChartLegendOptions,
    ChartHoverOptions,
    ChartTitleOptions,
    ChartTickOptions,
    ChartGridLineOptions,
    ChartScaleLabelOptions,
    LegacyTooltipItem,
    LegacyChartData,
    ChartType,
    getV3ChartType,
} from './chartjs-v2-types';

// Re-export types
export * from './chartjs-v2-types';

/**
 * Adapt a v2 chart type to v3
 * Handles special cases like 'horizontalBar' which no longer exists in v3
 */
export function adaptChartType (type: ChartType): ChartJsType {
    return getV3ChartType(type);
}

/**
 * Check if options need horizontal bar adjustment
 */
export function needsHorizontalBarOptions (type: ChartType): boolean {
    return type === 'horizontalBar';
}

/**
 * Check if a scale ID is a standard Chart.js v3 scale ID that doesn't need explicit axis property
 * In Chart.js v3, only these IDs are auto-recognized:
 * - 'x', 'y' - primary axes
 * - 'x1', 'y1', 'x2', 'y2', etc. - secondary axes
 *
 * Legacy v2 IDs like 'x-axis-0', 'y-axis-0' are NOT auto-recognized in v3
 * and require the explicit 'axis' property to be set.
 */
function isStandardScaleId (scaleId: string, axisType: 'x' | 'y'): boolean {
    // 'x', 'y' - standard v3 primary axis IDs
    if (scaleId === axisType) {
        return true;
    }
    // 'x1', 'y1', 'x2', 'y2', etc. - standard v3 secondary axis IDs
    if (new RegExp(`^${axisType}\\d+$`).test(scaleId)) {
        return true;
    }
    // All other IDs (including legacy 'x-axis-0', 'y-axis-0') need explicit axis property
    return false;
}

/**
 * Transform v2-style scale configuration to v3 format
 * v2: { xAxes: [...], yAxes: [...] }
 * v3: { x: {...}, y: {...}, y1: {...} }
 * @param scales - The v2 scales configuration
 * @param chartType - The chart type (needed to determine default scale types)
 */
export function transformScalesV2ToV3 (
    scales: ChartScalesSettings | undefined,
    chartType?: ChartType,
): Record<string, unknown> | undefined {
    if (!scales) {
        return undefined;
    }

    const v3Scales: Record<string, unknown> = {};

    // Transform xAxes array to keyed x scale(s)
    if (scales.xAxes && scales.xAxes.length > 0) {
        scales.xAxes.forEach((axis, index) => {
            const scaleId = axis.id || (index === 0 ? 'x' : `x${index}`);
            v3Scales[scaleId] = transformAxisV2ToV3(axis, 'x', scaleId, chartType);
        });
    }

    // Transform yAxes array to keyed y scale(s)
    if (scales.yAxes && scales.yAxes.length > 0) {
        scales.yAxes.forEach((axis, index) => {
            const scaleId = axis.id || (index === 0 ? 'y' : `y${index}`);
            v3Scales[scaleId] = transformAxisV2ToV3(axis, 'y', scaleId, chartType);
        });
    }

    return Object.keys(v3Scales).length > 0 ? v3Scales : undefined;
}

/**
 * Transform a single v2 axis configuration to v3 format
 * @param axis - The v2 axis configuration
 * @param axisType - The axis type ('x' or 'y') - required in v3 for custom scale IDs
 * @param scaleId - The scale ID being used - needed to determine if axis property is required
 * @param chartType - The chart type - needed to determine default scale type
 */
function transformAxisV2ToV3 (
    axis: ChartXAxe | ChartYAxe,
    axisType?: 'x' | 'y',
    scaleId?: string,
    chartType?: ChartType,
): Record<string, unknown> {
    const v3Axis: Record<string, unknown> = {};

    // In v3, custom scale IDs require explicit axis property
    // This tells Chart.js whether it's an x-axis or y-axis
    // Only set this for NON-STANDARD scale IDs to avoid interfering with chart types
    // that don't use scales (like doughnut/pie charts)
    if (axisType && scaleId && !isStandardScaleId(scaleId, axisType)) {
        v3Axis.axis = axisType;
    }

    // Direct property mappings
    // In v2, x-axes without explicit type default to 'category' when using labels
    // However, bubble and scatter charts need 'linear' scales by default
    // In v3, we need to be explicit about this
    // For horizontal bar charts (indexAxis: 'y'), the axes are swapped:
    // - Y-axis is the category axis (labels)
    // - X-axis is the value axis (linear)
    if (axis.type) {
        v3Axis.type = axis.type;
    } else if (chartType === 'horizontalBar') {
        // For horizontal bar charts, axes are swapped compared to regular bar charts
        if (axisType === 'y') {
            // Y-axis is the category axis (labels)
            v3Axis.type = 'category';
        } else if (axisType === 'x') {
            // X-axis is the value axis (linear) for horizontal bar charts
            v3Axis.type = 'linear';
        }
    } else if (axisType === 'x') {
        // Bubble and scatter charts need linear scales by default
        // These charts use {x, y, r} data points that need numeric positioning
        // Other charts (line, bar) typically need category scales for string labels
        if (chartType === 'bubble' || chartType === 'scatter') {
            v3Axis.type = 'linear';
        } else {
            v3Axis.type = 'category';
        }
    }
    if (axis.display !== undefined) {
        v3Axis.display = axis.display;
    }
    if (axis.position) {
        v3Axis.position = axis.position;
    }
    if (axis.offset !== undefined) {
        v3Axis.offset = axis.offset;
    }
    if (axis.stacked !== undefined) {
        v3Axis.stacked = axis.stacked;
    }

    // Transform gridLines → grid
    if (axis.gridLines) {
        v3Axis.grid = transformGridLinesV2ToV3(axis.gridLines);
    }

    // Transform scaleLabel → title
    if (axis.scaleLabel) {
        v3Axis.title = transformScaleLabelV2ToV3(axis.scaleLabel);
    }

    // Transform ticks
    // Pass axis type and scale type so tick callbacks can be properly wrapped
    if (axis.ticks) {
        // Determine the effective scale type for callback wrapping
        // Must match the logic used for setting v3Axis.type above
        let effectiveScaleType = axis.type;
        if (!effectiveScaleType) {
            if (chartType === 'horizontalBar') {
                // For horizontal bar charts, y-axis is category, x-axis is linear
                effectiveScaleType = axisType === 'y' ? 'category' : 'linear';
            } else if (axisType === 'x') {
                // Bubble and scatter charts use linear scales, others use category
                effectiveScaleType = (chartType === 'bubble' || chartType === 'scatter') ? 'linear' : 'category';
            }
        }
        const ticksResult = transformTicksV2ToV3(axis.ticks, axisType, effectiveScaleType);
        v3Axis.ticks = ticksResult.ticks;

        // Move min/max/suggestedMin/suggestedMax to scale level in v3
        if (ticksResult.scaleOptions) {
            Object.assign(v3Axis, ticksResult.scaleOptions);
        }
    }

    // Transform time options (for time scales)
    if ('time' in axis && axis.time) {
        v3Axis.time = axis.time;
    }

    // Bar chart specific options
    const xAxis = axis as ChartXAxe;
    if (xAxis.barPercentage !== undefined) {
        v3Axis.barPercentage = xAxis.barPercentage;
    }
    if (xAxis.categoryPercentage !== undefined) {
        v3Axis.categoryPercentage = xAxis.categoryPercentage;
    }
    if (xAxis.barThickness !== undefined) {
        v3Axis.barThickness = xAxis.barThickness;
    }
    if (xAxis.maxBarThickness !== undefined) {
        v3Axis.maxBarThickness = xAxis.maxBarThickness;
    }
    if (xAxis.minBarLength !== undefined) {
        v3Axis.minBarLength = xAxis.minBarLength;
    }

    return v3Axis;
}

/**
 * Transform v2 gridLines to v3 grid options
 */
function transformGridLinesV2ToV3 (gridLines: ChartGridLineOptions): Record<string, unknown> {
    const grid: Record<string, unknown> = {};

    if (gridLines.display !== undefined) {
        grid.display = gridLines.display;
    }
    if (gridLines.color) {
        grid.color = gridLines.color;
    }
    if (gridLines.borderDash) {
        grid.borderDash = gridLines.borderDash;
    }
    if (gridLines.borderDashOffset !== undefined) {
        grid.borderDashOffset = gridLines.borderDashOffset;
    }
    if (gridLines.lineWidth !== undefined) {
        grid.lineWidth = gridLines.lineWidth;
    }
    if (gridLines.drawBorder !== undefined) {
        grid.drawBorder = gridLines.drawBorder;
    }
    if (gridLines.drawOnChartArea !== undefined) {
        grid.drawOnChartArea = gridLines.drawOnChartArea;
    }
    if (gridLines.drawTicks !== undefined) {
        grid.drawTicks = gridLines.drawTicks;
    }
    if (gridLines.circular !== undefined) {
        grid.circular = gridLines.circular;
    }

    // tickMarkLength → tickLength in v3
    if (gridLines.tickMarkLength !== undefined) {
        grid.tickLength = gridLines.tickMarkLength;
    }

    // offsetGridLines → offset in v3
    if (gridLines.offsetGridLines !== undefined) {
        grid.offset = gridLines.offsetGridLines;
    }

    return grid;
}

/**
 * Transform v2 scaleLabel to v3 title options
 */
function transformScaleLabelV2ToV3 (scaleLabel: ChartScaleLabelOptions): Record<string, unknown> {
    const title: Record<string, unknown> = {};

    if (scaleLabel.display !== undefined) {
        title.display = scaleLabel.display;
    }

    // labelString → text
    if (scaleLabel.labelString) {
        title.text = scaleLabel.labelString;
    }

    // Font properties now nested under 'font'
    const font: Record<string, unknown> = {};
    if (scaleLabel.fontSize) {
        font.size = scaleLabel.fontSize;
    }
    if (scaleLabel.fontStyle) {
        font.style = scaleLabel.fontStyle;
    }
    if (scaleLabel.fontFamily) {
        font.family = scaleLabel.fontFamily;
    }
    if (Object.keys(font).length > 0) {
        title.font = font;
    }

    // fontColor → color
    if (scaleLabel.fontColor) {
        title.color = scaleLabel.fontColor;
    }

    if (scaleLabel.padding !== undefined) {
        title.padding = scaleLabel.padding;
    }

    return title;
}

/**
 * Transform v2 ticks to v3 format
 * Returns both ticks config and scale-level options (min, max, etc.)
 * @param ticks - The v2 ticks configuration
 * @param axisType - The axis type ('x' or 'y') - used for callback wrapping
 * @param scaleType - The scale type (e.g., 'category', 'linear', 'time') - used for callback wrapping
 */
interface TicksTransformResult {
    ticks: Record<string, unknown>;
    scaleOptions: Record<string, unknown> | undefined;
}

function transformTicksV2ToV3 (
    ticks: ChartTickOptions,
    axisType?: 'x' | 'y',
    scaleType?: string,
): TicksTransformResult {
    const v3Ticks: Record<string, unknown> = {};
    const scaleOptions: Record<string, unknown> = {};

    // Properties that stay in ticks
    if (ticks.display !== undefined) {
        v3Ticks.display = ticks.display;
    }
    if (ticks.autoSkip !== undefined) {
        v3Ticks.autoSkip = ticks.autoSkip;
    }
    if (ticks.autoSkipPadding !== undefined) {
        v3Ticks.autoSkipPadding = ticks.autoSkipPadding;
    }
    if (ticks.labelOffset !== undefined) {
        v3Ticks.labelOffset = ticks.labelOffset;
    }
    if (ticks.maxTicksLimit !== undefined) {
        v3Ticks.maxTicksLimit = ticks.maxTicksLimit;
    }
    if (ticks.stepSize !== undefined) {
        v3Ticks.stepSize = ticks.stepSize;
    }
    if (ticks.padding !== undefined) {
        v3Ticks.padding = ticks.padding;
    }
    if (ticks.mirror !== undefined) {
        v3Ticks.mirror = ticks.mirror;
    }

    // Font properties now nested under 'font'
    const font: Record<string, unknown> = {};
    if (ticks.fontSize) {
        font.size = ticks.fontSize;
    }
    if (ticks.fontStyle) {
        font.style = ticks.fontStyle;
    }
    if (ticks.fontFamily) {
        font.family = ticks.fontFamily;
    }
    if (Object.keys(font).length > 0) {
        v3Ticks.font = font;
    }

    // fontColor → color
    if (ticks.fontColor) {
        v3Ticks.color = ticks.fontColor;
    }

    // Transform tick callback for v2 → v3 compatibility
    // In v2 category scales, callback received (label, index, labels)
    // In v3 category scales, callback receives (tickValue, index, ticks) where tickValue is the INDEX
    // We need to wrap the callback to provide the actual label string for v2-style callbacks
    if (ticks.callback) {
        const originalCallback = ticks.callback;
        const isCategoryScale = scaleType === 'category' || (!scaleType && axisType === 'x');

        if (isCategoryScale) {
            // Wrap the callback to provide v2-compatible arguments
            // In v3, for category scales, the first argument is the tick index, not the label
            // We need to look up the actual label from chart.data.labels
            v3Ticks.callback = function (
                this: { chart: Chart },
                tickValue: string | number,
                index: number,
                ticksArray: unknown[],
            ) {
                // For category scales in v3, tickValue is the index
                // Get the actual label from chart data
                const chart = this.chart;
                const labels = chart?.data?.labels;
                // Use the label at this index if available, otherwise fall back to tickValue
                const actualLabel = labels && typeof tickValue === 'number' ? labels[tickValue] : tickValue;
                return originalCallback(actualLabel, index, ticksArray);
            };
        } else {
            // For non-category scales (linear, time, etc.), keep the callback as-is
            v3Ticks.callback = originalCallback;
        }
    }

    // Properties that move to scale level in v3
    if (ticks.min !== undefined) {
        scaleOptions.min = ticks.min;
    }
    if (ticks.max !== undefined) {
        scaleOptions.max = ticks.max;
    }
    if (ticks.suggestedMin !== undefined) {
        scaleOptions.suggestedMin = ticks.suggestedMin;
    }
    if (ticks.suggestedMax !== undefined) {
        scaleOptions.suggestedMax = ticks.suggestedMax;
    }
    if (ticks.beginAtZero !== undefined) {
        scaleOptions.beginAtZero = ticks.beginAtZero;
    }
    if (ticks.reverse !== undefined) {
        scaleOptions.reverse = ticks.reverse;
    }

    return {
        ticks: v3Ticks,
        scaleOptions: Object.keys(scaleOptions).length > 0 ? scaleOptions : undefined,
    };
}

/**
 * Transform v2 tooltip options to v3 format
 */
export function transformTooltipV2ToV3 (
    tooltips: ChartTooltipOptions | undefined,
    chartData?: LegacyChartData,
    chartType?: ChartType,
): Record<string, unknown> | undefined {
    if (!tooltips) {
        return undefined;
    }

    const v3Tooltip: Record<string, unknown> = {};

    // Direct mappings
    if (tooltips.enabled !== undefined) {
        v3Tooltip.enabled = tooltips.enabled;
    }
    if (tooltips.intersect !== undefined) {
        v3Tooltip.intersect = tooltips.intersect;
    }
    if (tooltips.position) {
        v3Tooltip.position = tooltips.position;
    }
    if (tooltips.backgroundColor) {
        v3Tooltip.backgroundColor = tooltips.backgroundColor;
    }
    if (tooltips.borderColor) {
        v3Tooltip.borderColor = tooltips.borderColor;
    }
    if (tooltips.borderWidth !== undefined) {
        v3Tooltip.borderWidth = tooltips.borderWidth;
    }
    if (tooltips.caretPadding !== undefined) {
        v3Tooltip.caretPadding = tooltips.caretPadding;
    }
    if (tooltips.caretSize !== undefined) {
        v3Tooltip.caretSize = tooltips.caretSize;
    }
    if (tooltips.cornerRadius !== undefined) {
        v3Tooltip.cornerRadius = tooltips.cornerRadius;
    }
    if (tooltips.displayColors !== undefined) {
        v3Tooltip.displayColors = tooltips.displayColors;
    }
    if (tooltips.multiKeyBackground) {
        v3Tooltip.multiKeyBackground = tooltips.multiKeyBackground;
    }
    if (tooltips.rtl !== undefined) {
        v3Tooltip.rtl = tooltips.rtl;
    }
    if (tooltips.textDirection) {
        v3Tooltip.textDirection = tooltips.textDirection;
    }

    // xPadding/yPadding → padding object
    if (tooltips.xPadding !== undefined || tooltips.yPadding !== undefined) {
        v3Tooltip.padding = {
            x: tooltips.xPadding ?? 6,
            y: tooltips.yPadding ?? 6,
        };
    }

    // Mode transformation: 'x-axis' and 'label' → 'index' in v3
    if (tooltips.mode) {
        const modeMap: Record<string, string> = {
            'x-axis': 'index',
            'label': 'index',
        };
        v3Tooltip.mode = modeMap[tooltips.mode] || tooltips.mode;
    }

    // Font properties now nested
    const titleFont: Record<string, unknown> = {};
    if (tooltips.titleFontSize) {
        titleFont.size = tooltips.titleFontSize;
    }
    if (tooltips.titleFontStyle) {
        titleFont.style = tooltips.titleFontStyle;
    }
    if (tooltips.titleFontFamily) {
        titleFont.family = tooltips.titleFontFamily;
    }
    if (Object.keys(titleFont).length > 0) {
        v3Tooltip.titleFont = titleFont;
    }
    if (tooltips.titleFontColor) {
        v3Tooltip.titleColor = tooltips.titleFontColor;
    }
    if (tooltips.titleSpacing !== undefined) {
        v3Tooltip.titleSpacing = tooltips.titleSpacing;
    }
    if (tooltips.titleMarginBottom !== undefined) {
        v3Tooltip.titleMarginBottom = tooltips.titleMarginBottom;
    }

    const bodyFont: Record<string, unknown> = {};
    if (tooltips.bodyFontSize) {
        bodyFont.size = tooltips.bodyFontSize;
    }
    if (tooltips.bodyFontStyle) {
        bodyFont.style = tooltips.bodyFontStyle;
    }
    if (tooltips.bodyFontFamily) {
        bodyFont.family = tooltips.bodyFontFamily;
    }
    if (Object.keys(bodyFont).length > 0) {
        v3Tooltip.bodyFont = bodyFont;
    }
    if (tooltips.bodyFontColor) {
        v3Tooltip.bodyColor = tooltips.bodyFontColor;
    }
    if (tooltips.bodySpacing !== undefined) {
        v3Tooltip.bodySpacing = tooltips.bodySpacing;
    }

    const footerFont: Record<string, unknown> = {};
    if (tooltips.footerFontSize) {
        footerFont.size = tooltips.footerFontSize;
    }
    if (tooltips.footerFontStyle) {
        footerFont.style = tooltips.footerFontStyle;
    }
    if (tooltips.footerFontFamily) {
        footerFont.family = tooltips.footerFontFamily;
    }
    if (Object.keys(footerFont).length > 0) {
        v3Tooltip.footerFont = footerFont;
    }
    if (tooltips.footerFontColor) {
        v3Tooltip.footerColor = tooltips.footerFontColor;
    }
    if (tooltips.footerSpacing !== undefined) {
        v3Tooltip.footerSpacing = tooltips.footerSpacing;
    }
    if (tooltips.footerMarginTop !== undefined) {
        v3Tooltip.footerMarginTop = tooltips.footerMarginTop;
    }

    // Transform callbacks
    if (tooltips.callbacks) {
        v3Tooltip.callbacks = transformTooltipCallbacksV2ToV3(tooltips.callbacks, chartData, chartType);
    }

    // Custom tooltip function: v2 'custom' → v3 'external'
    // v2: custom(tooltipModel) where `this` has _chart property
    // v3: external(context) where context = { chart, tooltip }
    if (tooltips.custom) {
        const v2CustomFn = tooltips.custom;
        v3Tooltip.external = function (context: { chart: unknown; tooltip: unknown }) {
            // Create a mock 'this' context that mimics v2 behavior
            // In v2, `this._chart` referred to the chart instance
            const v2Context = {
                _chart: context.chart,
            };
            // Call the v2 custom function with the tooltip as argument
            // and the v2-style context as 'this'
            v2CustomFn.call(v2Context, context.tooltip);
        };
    }

    return v3Tooltip;
}

/**
 * Transform v2 tooltip callbacks to v3 format
 * v2 callbacks: (tooltipItem, data) => ...
 * v3 callbacks: (context) => ...
 */
function transformTooltipCallbacksV2ToV3 (
    callbacks: ChartTooltipCallback,
    chartData?: LegacyChartData,
    chartType?: ChartType,
): Record<string, unknown> {
    const v3Callbacks: Record<string, unknown> = {};

    // For horizontal bar charts, xLabel and yLabel are swapped compared to regular bar charts
    // In v2 horizontal bar: xLabel = value (number), yLabel = category label
    // In v3 with indexAxis: 'y': context.label = category, context.formattedValue = value
    const isHorizontalBar = chartType === 'horizontalBar';

    // For bubble and scatter charts, xLabel/yLabel should be the numeric x/y coordinates
    // In v2: xLabel = x coordinate, yLabel = y coordinate
    // In v3: context.parsed.x and context.parsed.y contain these values
    const isBubbleOrScatter = chartType === 'bubble' || chartType === 'scatter';

    // Helper to create v3 callback from v2 callback
    const wrapCallback = (
        v2Callback: ((item: LegacyTooltipItem, data?: LegacyChartData) => string | string[] | void) | undefined,
    ) => {
        if (!v2Callback) {
            return undefined;
        }

        return (context: TooltipItem<ChartJsType>) => {
            // Determine xLabel and yLabel based on chart type
            let xLabel: string | number | undefined;
            let yLabel: string | number | undefined;

            if (isBubbleOrScatter) {
                // For bubble/scatter charts, use the parsed x/y coordinates
                xLabel = context.parsed.x;
                yLabel = context.parsed.y;
            } else if (isHorizontalBar) {
                // For horizontal bar charts, swap xLabel and yLabel to match v2 behavior
                xLabel = context.formattedValue;
                yLabel = context.label;
            } else {
                // Default behavior for other chart types
                xLabel = context.label;
                yLabel = context.formattedValue;
            }

            const legacyItem: LegacyTooltipItem = {
                datasetIndex: context.datasetIndex,
                index: context.dataIndex,
                xLabel,
                yLabel,
                label: context.label,
                value: context.formattedValue,
            };

            // Build legacy data object from context
            const legacyData: LegacyChartData = chartData || {
                labels: context.chart.data.labels as (string | string[])[],
                datasets: context.chart.data.datasets.map(ds => ({
                    label: ds.label ?? '',
                    data: ds.data as unknown[],
                })),
            };

            return v2Callback(legacyItem, legacyData);
        };
    };

    // Helper for array callbacks (title, footer, etc.)
    const wrapArrayCallback = (
        v2Callback: ((items: LegacyTooltipItem[], data?: LegacyChartData) => string | string[] | void) | undefined,
    ) => {
        if (!v2Callback) {
            return undefined;
        }

        return (contexts: TooltipItem<ChartJsType>[]) => {
            const legacyItems = contexts.map(context => {
                // Determine xLabel and yLabel based on chart type
                let xLabel: string | number | undefined;
                let yLabel: string | number | undefined;

                if (isBubbleOrScatter) {
                    // For bubble/scatter charts, use the parsed x/y coordinates
                    xLabel = context.parsed.x;
                    yLabel = context.parsed.y;
                } else if (isHorizontalBar) {
                    // For horizontal bar charts, swap xLabel and yLabel to match v2 behavior
                    xLabel = context.formattedValue;
                    yLabel = context.label;
                } else {
                    // Default behavior for other chart types
                    xLabel = context.label;
                    yLabel = context.formattedValue;
                }

                return {
                    datasetIndex: context.datasetIndex,
                    index: context.dataIndex,
                    xLabel,
                    yLabel,
                    label: context.label,
                    value: context.formattedValue,
                };
            });

            const legacyData: LegacyChartData = chartData || (contexts.length > 0 ? {
                labels: contexts[0].chart.data.labels as (string | string[])[],
                datasets: contexts[0].chart.data.datasets.map(ds => ({
                    label: ds.label ?? '',
                    data: ds.data as unknown[],
                })),
            } : { labels: [], datasets: [] });

            return v2Callback(legacyItems, legacyData);
        };
    };

    // Map all callback types
    if (callbacks.beforeTitle) {
        v3Callbacks.beforeTitle = wrapArrayCallback(callbacks.beforeTitle);
    }
    if (callbacks.title) {
        v3Callbacks.title = wrapArrayCallback(callbacks.title);
    }
    if (callbacks.afterTitle) {
        v3Callbacks.afterTitle = wrapArrayCallback(callbacks.afterTitle);
    }
    if (callbacks.beforeBody) {
        v3Callbacks.beforeBody = wrapArrayCallback(callbacks.beforeBody);
    }
    if (callbacks.beforeLabel) {
        v3Callbacks.beforeLabel = wrapCallback(callbacks.beforeLabel);
    }
    if (callbacks.label) {
        v3Callbacks.label = wrapCallback(callbacks.label);
    }
    if (callbacks.afterLabel) {
        v3Callbacks.afterLabel = wrapCallback(callbacks.afterLabel);
    }
    if (callbacks.afterBody) {
        v3Callbacks.afterBody = wrapArrayCallback(callbacks.afterBody);
    }
    if (callbacks.beforeFooter) {
        v3Callbacks.beforeFooter = wrapArrayCallback(callbacks.beforeFooter);
    }
    if (callbacks.footer) {
        v3Callbacks.footer = wrapArrayCallback(callbacks.footer);
    }
    if (callbacks.afterFooter) {
        v3Callbacks.afterFooter = wrapArrayCallback(callbacks.afterFooter);
    }

    // labelColor and labelTextColor need special handling
    if (callbacks.labelColor) {
        v3Callbacks.labelColor = (context: TooltipItem<ChartJsType>) => {
            const legacyItem: LegacyTooltipItem = {
                datasetIndex: context.datasetIndex,
                index: context.dataIndex,
            };
            return callbacks.labelColor?.(legacyItem, context.chart);
        };
    }

    if (callbacks.labelTextColor) {
        v3Callbacks.labelTextColor = (context: TooltipItem<ChartJsType>) => {
            const legacyItem: LegacyTooltipItem = {
                datasetIndex: context.datasetIndex,
                index: context.dataIndex,
            };
            return callbacks.labelTextColor?.(legacyItem, context.chart);
        };
    }

    return v3Callbacks;
}

/**
 * Transform v2 legend options to v3 format
 */
export function transformLegendV2ToV3 (legend: ChartLegendOptions | undefined): Record<string, unknown> | undefined {
    if (!legend) {
        return undefined;
    }

    const v3Legend: Record<string, unknown> = {};

    // Direct mappings
    if (legend.display !== undefined) {
        v3Legend.display = legend.display;
    }
    if (legend.position) {
        v3Legend.position = legend.position;
    }
    if (legend.fullWidth !== undefined) {
        v3Legend.fullWidth = legend.fullWidth;
    }
    if (legend.reverse !== undefined) {
        v3Legend.reverse = legend.reverse;
    }
    if (legend.rtl !== undefined) {
        v3Legend.rtl = legend.rtl;
    }
    if (legend.textDirection) {
        v3Legend.textDirection = legend.textDirection;
    }

    // Transform labels
    if (legend.labels) {
        const v3Labels: Record<string, unknown> = {};

        if (legend.labels.boxWidth !== undefined) {
            v3Labels.boxWidth = legend.labels.boxWidth;
        }
        if (legend.labels.padding !== undefined) {
            v3Labels.padding = legend.labels.padding;
        }
        if (legend.labels.usePointStyle !== undefined) {
            v3Labels.usePointStyle = legend.labels.usePointStyle;
        }
        if (legend.labels.generateLabels) {
            v3Labels.generateLabels = legend.labels.generateLabels;
        }
        if (legend.labels.filter) {
            v3Labels.filter = legend.labels.filter;
        }

        // Font properties
        const font: Record<string, unknown> = {};
        if (legend.labels.fontSize) {
            font.size = legend.labels.fontSize;
        }
        if (legend.labels.fontStyle) {
            font.style = legend.labels.fontStyle;
        }
        if (legend.labels.fontFamily) {
            font.family = legend.labels.fontFamily;
        }
        if (Object.keys(font).length > 0) {
            v3Labels.font = font;
        }

        if (legend.labels.fontColor) {
            v3Labels.color = legend.labels.fontColor;
        }

        if (Object.keys(v3Labels).length > 0) {
            v3Legend.labels = v3Labels;
        }
    }

    // Transform callbacks - v3 has different signature
    // Note: We need to store the original callback reference to call it properly
    const originalOnClick = legend.onClick;
    if (originalOnClick) {
        v3Legend.onClick = (event: ChartEvent, legendItem: LegendItem, legendInstance: unknown) => {
            // Adapt to v2 signature (event, legendItem)
            const mouseEvent = event.native as MouseEvent;
            originalOnClick(mouseEvent, legendItem, legendInstance);
        };
    }

    const originalOnHover = legend.onHover;
    if (originalOnHover) {
        v3Legend.onHover = (event: ChartEvent, legendItem: LegendItem, legendInstance: unknown) => {
            const mouseEvent = event.native as MouseEvent;
            originalOnHover(mouseEvent, legendItem, legendInstance);
        };
    }

    const originalOnLeave = legend.onLeave;
    if (originalOnLeave) {
        v3Legend.onLeave = (event: ChartEvent, legendItem: LegendItem, legendInstance: unknown) => {
            const mouseEvent = event.native as MouseEvent;
            originalOnLeave(mouseEvent, legendItem, legendInstance);
        };
    }

    return v3Legend;
}

/**
 * Transform v2 title options to v3 format
 */
export function transformTitleV2ToV3 (title: ChartTitleOptions | undefined): Record<string, unknown> | undefined {
    if (!title) {
        return undefined;
    }

    const v3Title: Record<string, unknown> = {};

    if (title.display !== undefined) {
        v3Title.display = title.display;
    }
    if (title.text) {
        v3Title.text = title.text;
    }
    if (title.position) {
        v3Title.position = title.position;
    }
    if (title.padding !== undefined) {
        v3Title.padding = title.padding;
    }

    // Font properties
    const font: Record<string, unknown> = {};
    if (title.fontSize) {
        font.size = title.fontSize;
    }
    if (title.fontStyle) {
        font.style = title.fontStyle;
    }
    if (title.fontFamily) {
        font.family = title.fontFamily;
    }
    if (title.lineHeight) {
        font.lineHeight = title.lineHeight;
    }
    if (Object.keys(font).length > 0) {
        v3Title.font = font;
    }

    if (title.fontColor) {
        v3Title.color = title.fontColor;
    }

    return v3Title;
}

/**
 * Transform v2 hover options to v3 format
 */
export function transformHoverV2ToV3 (hover: ChartHoverOptions | undefined): Record<string, unknown> | undefined {
    if (!hover) {
        return undefined;
    }

    const v3Hover: Record<string, unknown> = {};

    if (hover.mode !== undefined && hover.mode !== null) {
        // Mode transformation: 'x-axis' and 'label' → 'index' in v3
        const modeMap: Record<string, string> = {
            'x-axis': 'index',
            'label': 'index',
        };
        v3Hover.mode = modeMap[hover.mode] || hover.mode;
    }
    if (hover.intersect !== undefined) {
        v3Hover.intersect = hover.intersect;
    }
    if (hover.axis) {
        v3Hover.axis = hover.axis;
    }

    // animationDuration moved to animation.hover.duration in v3
    // We'll handle this at the top level

    return v3Hover;
}

/**
 * Transform v2 onHover callback to v3 format
 */
export function transformOnHoverV2ToV3 (
    onHover: ((event: MouseEvent, elements: unknown[]) => void) | undefined,
): ((event: ChartEvent, elements: ActiveElement[], chart: Chart) => void) | undefined {
    if (!onHover) {
        return undefined;
    }

    return (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const mouseEvent = event.native as MouseEvent;
        onHover(mouseEvent, elements);
    };
}

/**
 * Transform v2 onClick callback to v3 format
 */
export function transformOnClickV2ToV3 (
    onClick: ((event: MouseEvent, elements: unknown[]) => void) | undefined,
): ((event: ChartEvent, elements: ActiveElement[], chart: Chart) => void) | undefined {
    if (!onClick) {
        return undefined;
    }

    return (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const mouseEvent = event.native as MouseEvent;
        onClick(mouseEvent, elements);
    };
}

/**
 * Main transformation function: Convert v2 options to v3 format
 */
export function transformOptionsV2ToV3 (options: ChartOptionsV2, chartType?: ChartType): ChartJsOptions {
    const v3Options: Record<string, unknown> = {};

    // Layout options (same structure)
    if (options.layout) {
        v3Options.layout = options.layout;
    }

    // Responsiveness options (same structure)
    if (options.responsive !== undefined) {
        v3Options.responsive = options.responsive;
    }
    if (options.maintainAspectRatio !== undefined) {
        v3Options.maintainAspectRatio = options.maintainAspectRatio;
    }
    if (options.aspectRatio !== undefined) {
        v3Options.aspectRatio = options.aspectRatio;
    }

    // Animation options
    if (options.animation !== undefined) {
        v3Options.animation = options.animation;
    }

    // Build plugins object (v2 top-level → v3 plugins)
    const plugins: Record<string, unknown> = {};

    if (options.legend) {
        plugins.legend = transformLegendV2ToV3(options.legend);
    }

    if (options.title) {
        plugins.title = transformTitleV2ToV3(options.title);
    }

    if (options.tooltips) {
        plugins.tooltip = transformTooltipV2ToV3(options.tooltips, undefined, chartType);
    }

    if (Object.keys(plugins).length > 0) {
        v3Options.plugins = plugins;
    }

    // Transform scales
    // Pass chart type to determine correct default scale types
    // (e.g., bubble/scatter need linear, others need category)
    if (options.scales) {
        v3Options.scales = transformScalesV2ToV3(options.scales, chartType);
    }

    // For horizontal bar charts, ensure y-axis is properly configured as category scale
    // This is needed because Chart.js v3 with indexAxis: 'y' requires explicit category type
    // to properly use the chart's labels for the y-axis ticks
    if (chartType === 'horizontalBar') {
        const scales = (v3Options.scales || {}) as Record<string, Record<string, unknown>>;
        if (!scales.y) {
            scales.y = {};
        }
        if (!scales.y.type) {
            scales.y.type = 'category';
        }
        v3Options.scales = scales;
    }

    // Interaction/hover options
    // In v3, 'interaction' controls the default mode for both tooltips and hover
    // We set both 'interaction' and 'hover' to ensure consistent behavior
    if (options.hover) {
        const transformedHover = transformHoverV2ToV3(options.hover);
        v3Options.interaction = transformedHover;
        v3Options.hover = transformedHover;
    }

    if (options.onHover) {
        v3Options.onHover = transformOnHoverV2ToV3(options.onHover);
    }

    if (options.onClick) {
        v3Options.onClick = transformOnClickV2ToV3(options.onClick);
    }

    // Doughnut/Pie specific options
    // cutoutPercentage → cutout (now accepts percentage string or pixels)
    if (options.cutoutPercentage !== undefined) {
        v3Options.cutout = `${options.cutoutPercentage}%`;
    }
    if (options.rotation !== undefined) {
        v3Options.rotation = options.rotation;
    }
    if (options.circumference !== undefined) {
        v3Options.circumference = options.circumference;
    }

    // For horizontal bar charts, set indexAxis and interaction axis
    if (chartType === 'horizontalBar') {
        v3Options.indexAxis = 'y';

        // Set interaction axis to 'y' for proper tooltip triggering on horizontal bars
        // Without this, tooltips may not trigger correctly for bars in the middle
        // because Chart.js would look for nearest element along x-axis instead of y-axis
        const interaction = (v3Options.interaction || {}) as Record<string, unknown>;
        interaction.axis = 'y';
        v3Options.interaction = interaction;
    }

    // Preserve custom options (used by plugins)
    if (options.centerText) {
        v3Options.centerText = options.centerText;
    }
    if (options.backgroundAreas) {
        v3Options.backgroundAreas = options.backgroundAreas;
    }
    if (options.horizontalLines) {
        v3Options.horizontalLines = options.horizontalLines;
    }
    if (options.verticalLines) {
        v3Options.verticalLines = options.verticalLines;
    }
    if (options.drawLabelOnChart) {
        v3Options.drawLabelOnChart = options.drawLabelOnChart;
    }

    return v3Options as ChartJsOptions;
}

/**
 * Initialize Chart.js v3 with v2-compatible defaults
 * Call this once during app initialization
 */
export function initializeChartJsV2Defaults (): void {
    // In v3, defaults are at Chart.defaults directly, not Chart.defaults.global
    // Set common defaults that were in v2

    // Default colors - use type assertion to handle v3's Scriptable type
    if (!Chart.defaults.backgroundColor) {
        (Chart.defaults as unknown as Record<string, unknown>).backgroundColor = [
            '#97BBCD', // blue
            '#DCDCDC', // light grey
            '#F7464A', // red
            '#46BFBD', // green
            '#FDB45C', // yellow
            '#949FB1', // grey
            '#4D5360', // dark grey
        ];
    }

    // Default element styles
    if (Chart.defaults.elements) {
        if (Chart.defaults.elements.line) {
            Chart.defaults.elements.line.borderWidth = 2;
        }
        if (Chart.defaults.elements.bar) {
            Chart.defaults.elements.bar.borderWidth = 2;
        }
    }

    // Default plugins
    if (Chart.defaults.plugins) {
        if (Chart.defaults.plugins.legend) {
            Chart.defaults.plugins.legend.display = false;
        }
        if (Chart.defaults.plugins.tooltip) {
            Chart.defaults.plugins.tooltip.mode = 'index';
        }
    }
}

/**
 * Register a plugin with Chart.js v3
 * v2: Chart.pluginService.register(plugin)
 * v3: Chart.register(plugin)
 */
export function registerPlugin (plugin: Plugin): void {
    Chart.register(plugin);
}

/**
 * Unregister a plugin from Chart.js v3
 */
export function unregisterPlugin (plugin: Plugin): void {
    Chart.unregister(plugin);
}

/**
 * Get Chart constructor (for compatibility)
 */
export function getChartConstructor (): typeof Chart {
    return Chart;
}

/**
 * Helper to safely access defaults in v3 structure
 */
export const ChartDefaults = {
    get colors (): string[] {
        const bg = Chart.defaults.backgroundColor;
        if (Array.isArray(bg)) {
            return bg as string[];
        }
        return [];
    },
    set colors (value: string[]) {
        (Chart.defaults as unknown as Record<string, unknown>).backgroundColor = value;
    },
    get animation (): unknown {
        return Chart.defaults.animation;
    },
    set animation (value: unknown) {
        (Chart.defaults as unknown as Record<string, unknown>).animation = value;
    },
    get plugins () {
        return Chart.defaults.plugins;
    },
    get elements () {
        return Chart.defaults.elements;
    },
};
